export const totalContentV3QueryRequests = {
	getOpModesList: { enable: true },
	getOpModesListUniSea: { enable: true },
	getTonnesGroupedByOpModeAndDateRef: { enable: true },
	getAvgFuelPerDayPerModeBaseline: { enable: true },
	getTonnesGroupedByOpModeAndDateRefUniSea: { enable: true },
	getBaselineTimeAllocationPerModeUniSea: { enable: true },
	getBaselineAvgFuelPerModeUniSeaExport: { enable: true },
	getBaselineTimeAllocationPerModeUniSeaExport: { enable: true },
	getBaselineAvgFuelPerModeUniSea: { enable: true },
	getHydrodynamicPerformancePlantMaxKw: { enable: true },
	getFuelConsumptionPerDayUniSea: { enable: true },
	getFuelEfficiencyUniSea: { enable: true },
	getFuelConsumedPerEngineUniSea: { enable: true },
	getWeatherCardData: { enable: true },
	getWeatherCardDataUniSea: { enable: true },
	getPlantsMinuteTrack: { enable: true },
	getVoyagePerformance: { enable: true },
	getFacilitiesRadius: { enable: true },
	getTonnesPerDayPerMode: { enable: true },
	getTimeAllocationPerMode: { enable: true },
	getAvgLoadEnginesInUsePerMode: { enable: true },
	getUnitsOfEnginesInUseList: { enable: true },
	getTonnesPerDayPerModeUniSea: { enable: true },
	getTimeAllocationPerModeUniSea: { enable: true },
	getFuelEfficiencyHeatmap: { enable: true },
	getEventEfficiencyHeatmapLoad: { enable: true },
	getEventEfficiencyHeatmapCompute: { enable: true },
}

export type TotalContentV3QueryRequestKey = keyof typeof totalContentV3QueryRequests
export type TotalContentV3QueryRequestFlags = Record<
	TotalContentV3QueryRequestKey,
	{ enable: boolean }
>

export const TOTAL_CONTENT_V3_QUERY_REQUESTS_STORAGE_KEY = "totalContentV3QueryRequests"

export const totalContentV3QueryRequestKeys = Object.keys(
	totalContentV3QueryRequests
) as TotalContentV3QueryRequestKey[]

export const totalContentV3QueryRequestLabels: Record<TotalContentV3QueryRequestKey, string> = {
	getOpModesList: "opModeGetList",
	getOpModesListUniSea: "getOpModesV2",
	getTonnesGroupedByOpModeAndDateRef: "fuelPageGetTonnesGroupedByOpModeAndDateRef",
	getAvgFuelPerDayPerModeBaseline: "fuelPageGetAvgFuelPerDayPerMode",
	getTonnesGroupedByOpModeAndDateRefUniSea: "getFuelPerModeAndDateRefV2",
	getBaselineTimeAllocationPerModeUniSea: "getOpModeTimeAllocationV2",
	getBaselineAvgFuelPerModeUniSeaExport: "getAverageFuelConsumedPerModeV2",
	getBaselineTimeAllocationPerModeUniSeaExport: "getOpModeTimeAllocationV2",
	getBaselineAvgFuelPerModeUniSea: "getAverageFuelConsumedPerModeV2",
	getHydrodynamicPerformancePlantMaxKw: "hydrodynamicPerformanceGetPlantMaxKw",
	getFuelConsumptionPerDayUniSea: "getFuelConsumptionPerDaySamples",
	getFuelEfficiencyUniSea: "getFuelEfficiencySamples",
	getFuelConsumedPerEngineUniSea: "getFuelConsumedPerEngineV2",
	getWeatherCardData: "fuelPageGetWeatherCardData",
	getWeatherCardDataUniSea: "fuelPageGetWeatherCardDataUniSea",
	getPlantsMinuteTrack: "fuelPageGetPlantMinuteTrack",
	getVoyagePerformance: "fuelPageGetVoyagePerformance",
	getFacilitiesRadius: "getFacilities",
	getTonnesPerDayPerMode: "fuelPageGetPerformanceTotal",
	getTimeAllocationPerMode: "fuelPageGetTimeAllocation",
	getAvgLoadEnginesInUsePerMode: "fuelPageGetLoadOnMainEngines",
	getUnitsOfEnginesInUseList: "fuelPageGetMainEnginesInUse",
	getTonnesPerDayPerModeUniSea: "getAverageFuelConsumedPerModeV2",
	getTimeAllocationPerModeUniSea: "getOpModeTimeAllocationV2",
	getFuelEfficiencyHeatmap: "trimPerformanceGetHeatmap / hydrodynamicPerformanceGetHeatmaps",
	getEventEfficiencyHeatmapLoad: "foulingHeatmapLoad",
	getEventEfficiencyHeatmapCompute: "foulingHeatmapCompute",
}

let isTotalContentV3QueryRequestFilteringEnabled = false

const cloneCurrentFlags = (): TotalContentV3QueryRequestFlags =>
	Object.fromEntries(
		totalContentV3QueryRequestKeys.map((key) => [
			key,
			{ enable: totalContentV3QueryRequests[key].enable },
		])
	) as TotalContentV3QueryRequestFlags

const getEnabledFlags = (): TotalContentV3QueryRequestFlags =>
	Object.fromEntries(
		totalContentV3QueryRequestKeys.map((key) => [key, { enable: true }])
	) as TotalContentV3QueryRequestFlags

export const applyTotalContentV3QueryRequestFlags = (
	flags: Partial<Record<TotalContentV3QueryRequestKey, { enable: boolean }>>
) => {
	for (const key of totalContentV3QueryRequestKeys) {
		const flag = flags[key]
		if (flag !== undefined) {
			totalContentV3QueryRequests[key].enable = flag.enable
		}
	}
}

export const setTotalContentV3QueryRequestFilteringEnabled = (enabled: boolean) => {
	isTotalContentV3QueryRequestFilteringEnabled = enabled

	if (enabled === false) {
		applyTotalContentV3QueryRequestFlags(getEnabledFlags())
		return
	}

	getTotalContentV3QueryRequestFlags()
}

export const getTotalContentV3QueryRequestFlags = (): TotalContentV3QueryRequestFlags => {
	if (isTotalContentV3QueryRequestFilteringEnabled === false) {
		const flags = getEnabledFlags()
		applyTotalContentV3QueryRequestFlags(flags)
		return flags
	}

	const flags = cloneCurrentFlags()

	if (typeof window === "undefined") {
		return flags
	}

	const storedFlagsString = window.localStorage.getItem(
		TOTAL_CONTENT_V3_QUERY_REQUESTS_STORAGE_KEY
	)
	if (storedFlagsString === null) {
		return flags
	}

	try {
		const storedFlags = JSON.parse(storedFlagsString) as Partial<
			Record<TotalContentV3QueryRequestKey, { enable?: unknown }>
		>

		for (const key of totalContentV3QueryRequestKeys) {
			const storedFlag = storedFlags[key]
			if (typeof storedFlag?.enable === "boolean") {
				flags[key].enable = storedFlag.enable
			}
		}
	} catch {
		window.localStorage.removeItem(TOTAL_CONTENT_V3_QUERY_REQUESTS_STORAGE_KEY)
	}

	applyTotalContentV3QueryRequestFlags(flags)
	return flags
}

export const setTotalContentV3DisabledQueryRequest = (
	disabledKey: TotalContentV3QueryRequestKey | undefined
): TotalContentV3QueryRequestFlags => {
	if (isTotalContentV3QueryRequestFilteringEnabled === false) {
		const flags = getEnabledFlags()
		applyTotalContentV3QueryRequestFlags(flags)
		return flags
	}

	const flags = Object.fromEntries(
		totalContentV3QueryRequestKeys.map((key) => [key, { enable: key !== disabledKey }])
	) as TotalContentV3QueryRequestFlags

	applyTotalContentV3QueryRequestFlags(flags)

	if (typeof window !== "undefined") {
		window.localStorage.setItem(
			TOTAL_CONTENT_V3_QUERY_REQUESTS_STORAGE_KEY,
			JSON.stringify(flags)
		)
	}

	return flags
}

export const setTotalContentV3QueryRequestEnabled = (
	key: TotalContentV3QueryRequestKey,
	enable: boolean
): TotalContentV3QueryRequestFlags => {
	if (isTotalContentV3QueryRequestFilteringEnabled === false) {
		const flags = getEnabledFlags()
		applyTotalContentV3QueryRequestFlags(flags)
		return flags
	}

	const flags = getTotalContentV3QueryRequestFlags()
	flags[key].enable = enable
	applyTotalContentV3QueryRequestFlags(flags)

	if (typeof window !== "undefined") {
		window.localStorage.setItem(
			TOTAL_CONTENT_V3_QUERY_REQUESTS_STORAGE_KEY,
			JSON.stringify(flags)
		)
	}

	return flags
}

export const setTotalContentV3QueryRequestFlags = (
	flags: TotalContentV3QueryRequestFlags
): TotalContentV3QueryRequestFlags => {
	if (isTotalContentV3QueryRequestFilteringEnabled === false) {
		const enabledFlags = getEnabledFlags()
		applyTotalContentV3QueryRequestFlags(enabledFlags)
		return enabledFlags
	}

	applyTotalContentV3QueryRequestFlags(flags)

	if (typeof window !== "undefined") {
		window.localStorage.setItem(
			TOTAL_CONTENT_V3_QUERY_REQUESTS_STORAGE_KEY,
			JSON.stringify(flags)
		)
	}

	return flags
}
